import {useState, useContext, useEffect, useReducer, useCallback, Reducer, useRef} from "react";
import axios, {CancelTokenSource} from "axios";
import {APIAction, APIActionType, APIState, fetchRequest, FetchRequestStruct, isStrapiErrorResponse} from "./types/IAPI";
import {AuthContext} from "./authContext";
import hitApi from "./hitApi";
import {DevError, DevLog, DevWarn} from "./commonFunctions/devLog";
import {deepEqual} from "./commonFunctions/jsonObjManipulation";
import {NotificationContext} from "./notificationContext";
import {INotificationSeverity} from "./types/INotification";

//State management
function dataFetchReducer<T> (state: APIState<T>, action: APIAction<T>): APIState<T> {
	switch (action.type) {
		case APIActionType.FETCH_INIT:
			//cancel previous request
			if(state.isLoading && !!state.cancelToken){
				state.cancelToken.cancel();
				DevWarn("canceled api request, current state: ", state);
			}
			return {
				...state,
				cancelToken: axios.CancelToken.source(),
				isLoading: true,
				isError: false,
				statusCode: undefined
			};
		case APIActionType.FETCH_SUCCESS:
			if(action.payload === undefined) return state;
			return {
				...state,
				isLoading: false,
				isError: false,
				data: action.payload,
				cancelToken: null,
				statusCode: 200
			};
		case APIActionType.FETCH_FAILURE:
			return {
				...state,
				isLoading: false,
				statusCode: action.statusCode,
				isError: true,
				errorData: isStrapiErrorResponse(action.payload) ? action.payload : undefined,
				cancelToken: null
			};
		case APIActionType.DATA_UPDATE:
			if(!action.payload) return state;
			return {
				...state,
				data: action.payload
			};
		default:
			throw new Error();
	}
}

//caching should be taken care of outside of hook using usememo
/*
Typing: 
    R -> Request Type
    S -> State Type
 */

export type useApiReturn<S, R = any> = [
	APIState<S>, (requestNew?: fetchRequest<R> | null | string) => void,
	(data: S) => void];

export function useApi<S, R = any>(initialRequest?: fetchRequest<R>, initialData?: S): useApiReturn<S,R> {

	const {notify} = useContext(NotificationContext);
	const [request, setRequest] = useState<fetchRequest<R>>(initialRequest);
	const prevRequest = useRef<fetchRequest<R>>();
	const cancelTokenPrev = useRef<CancelTokenSource>();

	const {authenticated, jwt, config, logout} = useContext(AuthContext);
	const [state, dispatch] = useReducer<Reducer<APIState<S>, APIAction<S>>>(dataFetchReducer, {
		isLoading: false,
		isError: false,
		data: initialData,
		cancelToken: axios.CancelToken.source(),
		statusCode: undefined
	} as APIState<S>);

	useEffect(() => {
		DevLog("API AUTH", authenticated, jwt);
	}, [authenticated, jwt])

	const lastCall = useRef<number>(0); ///> used to throttle (should not call same call again for 1s)

	const callApi = useCallback(async (r: fetchRequest<R>) => {
		if(r != null && config){
			dispatch({type: APIActionType.FETCH_INIT});
			lastCall.current = Date.now();

			//cancel previous if running
			if(cancelTokenPrev.current) cancelTokenPrev.current.cancel();

			//create new cancelation token for current call
			const cancelTokenSource = axios.CancelToken.source();
			cancelTokenPrev.current = cancelTokenSource;

			hitApi<S, R>(r, config, jwt, cancelTokenSource)
				.then((result) => {
					dispatch({type: APIActionType.FETCH_SUCCESS, payload: result, statusCode: 200});
					cancelTokenPrev.current = undefined;
				}).catch((error) => {
					DevError("API Error", error);
					if(!error.response){
						notify("Please check your network connection", "Network Error", INotificationSeverity.error, 10000)
					}
					else if(error.response.status === 401) {
						logout();
						//Would be good attempt silent sign in here if Strapi allows
					}
					else if (error.response.status === 400) {
						DevWarn("API", "400 Failed Request - Bad request", error);
					}
					else if (error.response.status === 403) {
						DevWarn("API", "403 Forbidden Request - Bad request", error);
					}

					dispatch({type: APIActionType.FETCH_FAILURE, statusCode: error.response?.status ?? 500, payload: error.response.data});
				}).finally(() => {
					prevRequest.current = r;
				});
		}
	}, [jwt, authenticated]);

	useEffect(() => {
		//recall if no fetch object passed using (if valid) last fetch request 
		if(!request && !!prevRequest.current){
			setRequest(prevRequest.current);
			prevRequest.current = undefined;
		}
		else if(request){
			const equalsPrev = deepEqual(prevRequest.current, request);
			const struct = request as FetchRequestStruct<S>;

			//throttle to 1 call per second
			if((struct?.throttle !== false) && ((Date.now() - lastCall.current) < 1000) && equalsPrev){
				DevWarn("Too many calls! Throttling. Please restructure code - ", request);
				return;
			}

			//cache: do not update if same request
			if(equalsPrev && ((struct.cache ?? true) !== false)){
				DevWarn("Using cached - Please restructure code or call purposely via request method with empty params", request);
				return;
			}

			callApi(request);
		}
	}, [request, callApi]);

	const setData = useCallback((data: S) => {
		dispatch({type: APIActionType.DATA_UPDATE, payload: data});
	}, []);

	return [state, setRequest, setData];
}

export default useApi;